import React, { useEffect, useState } from "react";

import {
  Button,
  Col,
  CustomInput,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";

import Loader from "../../../Loader";
import InformationModal from "../../../InformationModal";
import { useJobDetails } from "../../../../providers/jobDetailsProvider";
import { jobsApi } from "../../../../services/jobServices";
import { commonApi } from "../../../../services/commonServices";
import { useAuth } from "../../../../providers/authProvider";
import { utils } from "../../../../utils/utils";

const YES = "Yes";
const NO = "No";

const EACH = "Each";
const LFT = "Lft";
const LOT = "Lot";

const JobSummaryModal = ({ onClose, onSubmit }) => {
  const [jobDetails] = useJobDetails();
  const [authContext] = useAuth();
  const [loading, setLoading] = useState();
  const [job, setJob] = useState({});
  const [billingTerms, setBillingTerms] = useState([]);

  const isAdmin = utils.isAdminRole(authContext.currentUser?.role?.id);

  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
  });

  const doSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const data = {
        ...job,
        hardwareIncluded: job.hardwareIncluded,
        leadTime: job.leadTime,
        sqNotes: job.sqNotes,
        revisionNumber: job.revisionNumber,
        ...(isAdmin && { billingTermId: job.billingTermId }),
      };
      jobsApi.updateJob(data);
      onSubmit();
    } catch (err) {
      return setInformationModal({
        isOpen: true,
        title: "Update Job Contract Specification",
        body:
          err?.response?.data[0].msg || "There was an error with your request.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setJob(jobDetails.job);
  }, [jobDetails.job]);

  useEffect(() => {
    const fetchBillingTerms = async () => {
      try {
        const termsGotten = await commonApi.getBillingTerms();
        setBillingTerms(termsGotten);
      } catch (err) {
        console.error("Error fetching billing terms:", err);
      }
    };
    if (isAdmin) {
      fetchBillingTerms();
    }
  }, [isAdmin]);

  const closeBtn = (
    <Button className="close" color="none" onClick={onClose}>
      &times;
    </Button>
  );

  return informationModal.isOpen ? (
    <InformationModal
      title={informationModal.title}
      body={informationModal.body}
      onClose={() =>
        setInformationModal({ isOpen: false, title: "", body: "" })
      }
    />
  ) : (
    <Modal isOpen={true} size="lg">
      <Form onSubmit={doSubmit}>
        <ModalHeader
          className="d-flex justify-content-between"
          close={closeBtn}
        >
          Update Sales Quotation
        </ModalHeader>
        <ModalBody>
          <Row>
            {loading ? (
              <Loader size="sm" />
            ) : (
              <div className="col">
                <Row className="col-12 mb-3">
                  <Col md={3}>
                    <Label> Hardware Included? </Label>
                    <CustomInput
                      required
                      type="select"
                      id="hardwareIncluded"
                      value={job.hardwareIncluded || ""}
                      onChange={(e) =>
                        setJob({
                          ...job,
                          hardwareIncluded: e.target.value,
                        })
                      }
                    >
                      <option value={""} disabled>
                        Select ...
                      </option>
                      <option value={YES}>Yes</option>
                      <option value={NO}>No</option>
                    </CustomInput>
                  </Col>
                  <Col md={3}>
                    <Label>Lead Time</Label>
                    <span className="text-danger ml-2">*</span>
                    <Input
                      required
                      id="leadTime"
                      placeholder="Lead Time..."
                      value={job.leadTime || ""}
                      onChange={(e) =>
                        setJob({
                          ...job,
                          leadTime: e.target.value,
                        })
                      }
                    />
                  </Col>
                  <Col md={3}>
                    <Label> UOM? </Label>
                    <CustomInput
                      required
                      type="select"
                      id="uom"
                      value={job.uom || ""}
                      onChange={(e) =>
                        setJob({
                          ...job,
                          uom: e.target.value,
                        })
                      }
                    >
                      <option value={""} disabled>
                        Select ...
                      </option>
                      <option value={EACH}>Each</option>
                      <option value={LOT}>Lot</option>
                      <option value={LFT}>Lft</option>
                    </CustomInput>
                  </Col>
                  <Col md={3}>
                    <Label>Revision Number</Label>
                    <span className="text-danger ml-2">*</span>
                    <Input
                      required
                      id="revisionNumber"
                      placeholder="Revision Number..."
                      value={job.revisionNumber || ""}
                      onChange={(e) =>
                        setJob({
                          ...job,
                          revisionNumber: e.target.value,
                        })
                      }
                    />
                  </Col>
                </Row>
                {isAdmin && (
                  <Row className="col-12 mb-3">
                    <Col md={3}>
                      <Label>Billing Terms</Label>
                      <CustomInput
                        type="select"
                        id="detailer"
                        value={job?.billingTermId || ""}
                        onChange={(e) =>
                          setJob({
                            ...job,
                            billingTermId: +e.target.value,
                          })
                        }
                      >
                        <option value="" disabled>
                          Select A Billing Term
                        </option>
                        {billingTerms.map((term, index) => (
                          <option value={term.id} key={`term-${index}`}>
                            {term.name}
                          </option>
                        ))}
                      </CustomInput>
                    </Col>
                  </Row>
                )}
                <Row className="col-12 mb-3">
                  <Col md={12}>
                    <Label>SQ Notes</Label>
                    <Input
                      placeholder="SQ Notes..."
                      maxLength="4096"
                      type="textarea"
                      rows={7}
                      value={job.sqNotes || ""}
                      onChange={(e) =>
                        setJob({
                          ...job,
                          sqNotes: e.target.value,
                        })
                      }
                    />
                  </Col>
                </Row>
              </div>
            )}
          </Row>
        </ModalBody>
        <ModalFooter>
          <Col>
            <Row className="justify-content-between">
              <Button color={"secondary"} onClick={onClose}>
                Cancel
              </Button>
              <Button color={"primary"} type="submit">
                Save
              </Button>
            </Row>
          </Col>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default JobSummaryModal;
